import { gql } from '@apollo/client'

export const SWMS_SETTINGS_ATTRIBUTES = gql`
  fragment swmsSettingsAttributes on SWMSSettings {
    includeSWMSChecklistInPDFExport
    includeSWMSActivityInPDFExport
    showBuildPassSWMSTemplatesOption
    showStartFromScratchSWMSOption
    allowSavingNewSWMSTemplates
    allowOverridingExistingSWMSTemplates
    autoApproveSWMSForBuilder
    autoApproveSWMSForSubcontractorFromTemplate
    sharedSWMSTemplateIds
    allowExpiringSWMS
    defaultSWMSHazardResponsible
    librarySwmsExpiryDays
  }
`

export const CHECKLIST_SETTINGS_ATTRIBUTES = gql`
  fragment checklistSettingsAttributes on ChecklistSettings {
    publicChecklistIds
    checklistsAvailableForStaff
    checklistCategories {
      id
      label
      icon
      colour
    }
    checklistCategoriesInNavigation
    onlySendActionsAfterChecklistCompleted
    requireProjectInduction
    showSubcontractorColumnInChecklistTable
  }
`

export const INDUCTION_SETTINGS_ATTRIBUTES = gql`
  fragment inductionSettingsAttributes on InductionSettings {
    projectIdsWithInductionDisabled
    inductionsDisabledForAllProjects
    preventWorkersAccessingDocumentsUntilApproved
    inductionScanRequirementDisabledForAllProjects
    projectIdsWithInductionScanRequirementDisabled
    projectManagersCanEditInductionTemplates
    projectIdsWithVisitorAccessToPublicDocumentsAfterInduction
    preventInductionWithoutApprovedSwms
  }
`

export const PROJECT_SPECIFIC_INDUCTION_SETTINGS_ATTRIBUTES = gql`
  fragment projectSpecificInductionSettingsAttributes on ProjectSpecificInductionSettings {
    siteInductionCode
    siteInductionCodeDescription
    projectManagersCanEditInductionTemplates
  }
`

export const SIGN_ON_SETTINGS_ATTRIBUTES = gql`
  fragment signOnSettingsAttributes on SignOnSettings {
    projectIdsWithSignOnScanRequirementDisabled
    signOnScanRequirementDisabledForAllProjects
    geolocationSignOnOffRemindersEnabledForAllProjects
    projectIdsWithGeolocationSignOnOffRemindersEnabled
    preventSignOnWithoutValidTickets
    preventSignOnUntilInductionApproved
    geolocationAutoSignOnOffEnabledForAllProjects
  }
`

export const PROJECT_SPECIFIC_PERMIT_SETTINGS_ATTRIBUTES = gql`
  fragment projectSpecificPermitSettingsAttributes on ProjectSpecificPermitSettings {
    permitAdminNotificationsIds
    authorisedWorkersForPermitRequests
  }
`

export const PROJECT_SPECIFIC_WORKER_PERMIT_SETTINGS_ATTRIBUTES = gql`
  fragment projectSpecificWorkerPermitSettingsAttributes on ProjectSpecificWorkerPermitSettings {
    isAuthorisedToRequestPermits
  }
`

export const AI_TOOLS_SETTINGS_ATTRIBUTES = gql`
  fragment aiToolsSettingsAttributes on AiToolsSettings {
    aiToolsEnabled

    enabledByName
    enabledByDate

    checklistAssistantEnabled
    imageTaggingEnabled
    siteDiarySummaryEnabled
    toolboxSummaryEnabled
    defectEnhancementEnabled
    documentSummaryEnabled
    contentTaggingEnabled
    locationImportEnabled
    contextualActionGeneratorEnabled

    requestedAiTools
  }
`

export const USER_SETTINGS_ATTRIBUTES = gql`
  fragment userSettingsAttributes on UserSettings {
    favourites
  }
`

export const GET_SWMS_SETTINGS = gql`
  query swmsSettings_UI {
    swmsSettings {
      ...swmsSettingsAttributes
    }
  }
  ${SWMS_SETTINGS_ATTRIBUTES}
`

export const UPDATE_SWMS_SETTINGS = gql`
  mutation updateSWMSSettings_UI($input: UpdateSWMSSettingInput!) {
    updateSWMSSettings(input: $input) {
      ...swmsSettingsAttributes
    }
  }
  ${SWMS_SETTINGS_ATTRIBUTES}
`

export const TIMESHEET_SETTINGS_ATTRIBUTES = gql`
  fragment timesheetSettingsAttributes on TimesheetSettings {
    isPrimaryApproverOfProjects {
      projectId
      isPrimaryApprover
    }
    requiresSecondaryApproval
    isSecondaryApprover
    timesheetEntryNotes
    timesheetStartEndTimes
    jobPacCsvExport
    timeTypes {
      id
      label
      icon
      colour
    }
  }
`

export const GET_TIMESHEET_SETTINGS = gql`
  query timesheetSettings_UI {
    timesheetSettings {
      ...timesheetSettingsAttributes
      primaryApprovers {
        projectId
        adminUserIds
      }
      secondaryApprovers {
        id
        user {
          id
          firstName
          lastName
        }
      }
      secondaryApproverIds
      subcontractorsWhitelist {
        id
        company {
          name
        }
      }
    }
  }
  ${TIMESHEET_SETTINGS_ATTRIBUTES}
`

export const GET_WORKER_TIMESHEET_SETTINGS = gql`
  query workerTimesheetSettings_UI($projectId: String!) {
    workerTimesheetSettings(projectId: $projectId) {
      isTimesheetsEnabledForMe
      timesheetEntryNotes
      timesheetStartEndTimes
      timeTypes {
        id
        label
        icon
        colour
      }
    }
  }
`

export const UPDATE_TIMESHEET_SETTINGS = gql`
  mutation updateTimesheetSettings_UI($input: UpdateTimesheetSettingInput!) {
    updateTimesheetSettings(input: $input) {
      ...timesheetSettingsAttributes
    }
  }
  ${TIMESHEET_SETTINGS_ATTRIBUTES}
`

export const PROCORE_SETTINGS_ATTRIBUTES = gql`
  fragment procoreSettingsAttributes on ProcoreSettings {
    syncProjects
    projectFields
    syncSubcontractors
    subcontractorFields
    syncInductions
    syncSiteDiaries
    syncToolboxes
    syncSwms
    syncChecklists
    notifyForProjects
    notifyForSubcontractors
    syncSiteAttendanceToDailyLog_Manpower
    syncSiteAttendanceToDailyLog_Timecards
  }
`

export const GET_PROCORE_SETTINGS = gql`
  query procoreSettings_UI {
    procoreSettings {
      ...procoreSettingsAttributes
    }
  }
  ${PROCORE_SETTINGS_ATTRIBUTES}
`

export const UPDATE_PROCORE_SETTINGS = gql`
  mutation updateProcoreSettings_UI($input: UpdateProcoreSettingInput!) {
    updateProcoreSettings(input: $input) {
      ...procoreSettingsAttributes
    }
  }
  ${PROCORE_SETTINGS_ATTRIBUTES}
`

export const GET_GLOBAL_INDUCTION_SETTINGS = gql`
  query globalInductionSettings_UI {
    inductionSettings {
      ...inductionSettingsAttributes
    }
  }
  ${INDUCTION_SETTINGS_ATTRIBUTES}
`

export const GET_PROJECT_SPECIFIC_INDUCTION_SETTINGS = gql`
  query projectSpecificInductionSettings_UI($projectId: String!) {
    projectSpecificInductionSettings(projectId: $projectId) {
      ...projectSpecificInductionSettingsAttributes
    }
  }
  ${PROJECT_SPECIFIC_INDUCTION_SETTINGS_ATTRIBUTES}
`

export const UPDATE_INDUCTION_SETTINGS = gql`
  mutation updateInductionSettings_UI($input: UpdateInductionSettingInput!) {
    updateInductionSettings(input: $input) {
      ...inductionSettingsAttributes
    }
  }
  ${INDUCTION_SETTINGS_ATTRIBUTES}
`

export const UPDATE_PROJECT_SPECIFIC_INDUCTION_SETTINGS = gql`
  mutation updateProjectSpecificInductionSettings_UI(
    $projectId: String!
    $input: UpdateProjectSpecificInductionSettingInput!
  ) {
    updateProjectSpecificInductionSettings(
      projectId: $projectId
      input: $input
    ) {
      ...projectSpecificInductionSettingsAttributes
    }
  }
  ${PROJECT_SPECIFIC_INDUCTION_SETTINGS_ATTRIBUTES}
`

export const GET_SIGN_ON_SETTINGS = gql`
  query signOnSettings_UI {
    signOnSettings {
      ...signOnSettingsAttributes
    }
  }
  ${SIGN_ON_SETTINGS_ATTRIBUTES}
`

export const UPDATE_SIGN_ON_SETTINGS = gql`
  mutation updateSignOnSettings_UI($input: UpdateSignOnSettingInput!) {
    updateSignOnSettings(input: $input) {
      ...signOnSettingsAttributes
    }
  }
  ${SIGN_ON_SETTINGS_ATTRIBUTES}
`

export const GET_PROJECT_SPECIFIC_PERMIT_SETTINGS = gql`
  query projectSpecificPermitSettings_UI($projectId: String!) {
    projectSpecificPermitSettings(projectId: $projectId) {
      ...projectSpecificPermitSettingsAttributes
    }
  }
  ${PROJECT_SPECIFIC_PERMIT_SETTINGS_ATTRIBUTES}
`

export const GET_PROJECT_SPECIFIC_WORKER_PERMIT_SETTINGS = gql`
  query projectSpecificWorkerPermitSettings_UI($projectId: String!) {
    projectSpecificWorkerPermitSettings(projectId: $projectId) {
      ...projectSpecificWorkerPermitSettingsAttributes
    }
  }
  ${PROJECT_SPECIFIC_WORKER_PERMIT_SETTINGS_ATTRIBUTES}
`

export const UPDATE_PROJECT_SPECIFIC_PERMIT_SETTINGS = gql`
  mutation updateProjectSpecificPermitSettings_UI(
    $projectId: String!
    $input: UpdateProjectSpecificPermitSettingsInput!
  ) {
    updateProjectSpecificPermitSettings(projectId: $projectId, input: $input) {
      ...projectSpecificPermitSettingsAttributes
    }
  }
  ${PROJECT_SPECIFIC_PERMIT_SETTINGS_ATTRIBUTES}
`

export const GET_CHECKLIST_SETTINGS = gql`
  query checklistSettings_UI {
    checklistSettings {
      ...checklistSettingsAttributes
    }
  }
  ${CHECKLIST_SETTINGS_ATTRIBUTES}
`

export const UPDATE_CHECKLIST_SETTINGS = gql`
  mutation updateChecklistSettings_UI($input: UpdateChecklistSettingInput!) {
    updateChecklistSettings(input: $input) {
      ...checklistSettingsAttributes
    }
  }
  ${CHECKLIST_SETTINGS_ATTRIBUTES}
`

export const SUBCONTRACTOR_SETTINGS_ATTRIBUTES = gql`
  fragment subcontractorSettingsAttributes on SubcontractorSettings {
    expiredInsurancesEmails
    notifyAdminUsers
    customInsurancesTypes
  }
`

export const GET_SUBCONTRACTOR_SETTINGS = gql`
  query subcontractorSettings_UI {
    subcontractorSettings {
      ...subcontractorSettingsAttributes
    }
  }
  ${SUBCONTRACTOR_SETTINGS_ATTRIBUTES}
`

export const GET_PUBLIC_SUBCONTRACTOR_SETTINGS = gql`
  query publicSubcontractorSettings_UI($builderId: String!) {
    publicSubcontractorSettings(builderId: $builderId) {
      customInsurancesTypes
    }
  }
`

export const UPDATE_SUBCONTRACTOR_SETTINGS = gql`
  mutation updateSubcontractorSettings_UI(
    $input: UpdateSubcontractorSettingInput!
  ) {
    updateSubcontractorSettings(input: $input) {
      ...subcontractorSettingsAttributes
    }
  }
  ${SUBCONTRACTOR_SETTINGS_ATTRIBUTES}
`

export const ADD_PUBLIC_CHECKLIST = gql`
  mutation addPublicChecklist_UI($checklistId: String!) {
    addPublicChecklist(checklistId: $checklistId) {
      publicChecklistIds
    }
  }
`

export const REMOVE_PUBLIC_CHECKLIST = gql`
  mutation removePublicChecklist_UI($checklistId: String!) {
    removePublicChecklist(checklistId: $checklistId) {
      publicChecklistIds
    }
  }
`

export const GET_PLATFORMACTION_SETTINGS = gql`
  query platformActionSettings_UI {
    platformActionSettings {
      types {
        id
        label
        icon
        colour
      }
      priorities {
        id
        label
        icon
        colour
      }
      sendReminderEnabled
      sendReminderDays
      overdueActionsReminder
      overdueActionsReminderFrequency
      overdueActionsReminderAdminUsers
      actionsDueSoonReminder
      actionsDueSoonReminderDays
      actionsDueSoonReminderFrequency
      actionsDueSoonReminderAdminUsers
      allowSubcontractorsToCloseActions
    }
  }
`

export const GET_PUBLIC_PLATFORMACTION_SETTINGS = gql`
  query publicPlatformActionSettings_UI($builderId: String!) {
    publicPlatformActionSettings(builderId: $builderId) {
      types {
        id
        label
        icon
        colour
      }
      priorities {
        id
        label
        icon
        colour
      }
    }
  }
`

export const UPDATE_PLATFORMACTION_SETTINGS = gql`
  mutation updatePlatformActionSettings_UI(
    $input: UpdatePlatformActionSettingInput!
  ) {
    updatePlatformActionSettings(input: $input) {
      types {
        id
        label
        icon
        colour
      }
      priorities {
        id
        label
        icon
        colour
      }
      sendReminderEnabled
      sendReminderDays
      overdueActionsReminder
      overdueActionsReminderFrequency
      overdueActionsReminderAdminUsers
      actionsDueSoonReminder
      actionsDueSoonReminderDays
      actionsDueSoonReminderFrequency
      actionsDueSoonReminderAdminUsers
      allowSubcontractorsToCloseActions
    }
  }
`

export const GET_HOURS_BUDGET_SETTINGS = gql`
  query hoursBudgetSettings_UI($projectId: String!) {
    hoursBudgetSettings(projectId: $projectId) {
      hoursBudget
    }
  }
`

export const UPDATE_HOURS_BUDGET_SETTINGS = gql`
  mutation updateHoursBudgetSettings_UI(
    $projectId: String!
    $input: UpdateHoursBudgetSettingsInput!
  ) {
    updateHoursBudgetSettings(projectId: $projectId, input: $input) {
      hoursBudget
    }
  }
`

export const GET_DEFECT_SETTINGS = gql`
  query defectSettings_UI {
    defectSettings {
      allowWorkerAssignees
      notifyAssigneesOnDefectCreated
    }
  }
`

export const UPDATE_DEFECT_SETTINGS = gql`
  mutation updateDefectSettings_UI($input: UpdateDefectSettingsInput!) {
    updateDefectSettings(input: $input) {
      allowWorkerAssignees
      notifyAssigneesOnDefectCreated
    }
  }
`

export const GET_BUILDER_REGISTER_SETTINGS = gql`
  query builderRegisterSettings_UI {
    builderRegisterSettings {
      onlyAllowReviewedRegisterItemChecklists
    }
  }
`

export const UPDATE_BUILDER_REGISTER_SETTINGS = gql`
  mutation updateBuilderRegisterSettings_UI(
    $input: UpdateBuilderRegisterSettingsInput!
  ) {
    updateBuilderRegisterSettings(input: $input) {
      onlyAllowReviewedRegisterItemChecklists
    }
  }
`

export const GET_PREQUALIFICATION_SETTINGS = gql`
  query prequalificationSettings_UI {
    prequalificationSettings {
      notifyAdminUsers
      notifyAdminUsersOnApproval
    }
  }
`

export const UPDATE_PREQUALIFICATION_SETTINGS = gql`
  mutation updatePrequalificationSettings_UI(
    $input: UpdatePrequalificationSettingsInput!
  ) {
    updatePrequalificationSettings(input: $input) {
      notifyAdminUsers
      notifyAdminUsersOnApproval
    }
  }
`

export const GET_STYLING_SETTINGS = gql`
  query stylingSettings_UI {
    stylingSettings {
      primaryColour
      buttonTextColour
      primaryTextColour
    }
  }
`

export const UPDATE_STYLING_SETTINGS = gql`
  mutation updateStylingSettings_UI($input: UpdateStylingSettingsInput!) {
    updateStylingSettings(input: $input) {
      primaryColour
      buttonTextColour
      primaryTextColour
    }
  }
`

export const GET_COMMUNICATION_SETTINGS = gql`
  query communicationSettings_UI {
    communicationSettings {
      allowCompanyWideCommunication
    }
  }
`

export const UPDATE_COMMUNICATION_SETTINGS = gql`
  mutation updateCommunicationSettings_UI(
    $input: UpdateCommunicationSettingsInput!
  ) {
    updateCommunicationSettings(input: $input) {
      allowCompanyWideCommunication
    }
  }
`

export const GET_AI_TOOLS_SETTINGS = gql`
  query aiToolsSettings_UI {
    aiToolsSettings {
      ...aiToolsSettingsAttributes
    }
  }
  ${AI_TOOLS_SETTINGS_ATTRIBUTES}
`

export const UPDATE_AI_TOOLS_SETTINGS = gql`
  mutation updateAiToolsSettings_UI($input: UpdateAiToolsSettingsInput!) {
    updateAiToolsSettings(input: $input) {
      ...aiToolsSettingsAttributes
    }
  }
  ${AI_TOOLS_SETTINGS_ATTRIBUTES}
`

export const TOGGLE_AI_TOOLS_ENABLED_SETTINGS = gql`
  mutation toggleAiToolsEnabledSettings_UI {
    toggleAiToolsEnabledSettings {
      ...aiToolsSettingsAttributes
    }
  }
  ${AI_TOOLS_SETTINGS_ATTRIBUTES}
`

export const REQUEST_AI_TOOL_SETTINGS = gql`
  mutation requestAiToolSettings_UI($tool: String!) {
    requestAiToolSettings(tool: $tool) {
      ...aiToolsSettingsAttributes
    }
  }
  ${AI_TOOLS_SETTINGS_ATTRIBUTES}
`

export const REGISTER_AI_TOOLS_USAGE = gql`
  mutation registerAIToolsUsage_UI($tool: String!, $action: String!) {
    registerAIToolsUsage(tool: $tool, action: $action)
  }
`

export const UPDATE_USER_SETTINGS = gql`
  mutation updateUserSettings_UI($input: UpdateUserSettings!) {
    updateUserSettings(input: $input) {
      ...userSettingsAttributes
    }
  }
  ${USER_SETTINGS_ATTRIBUTES}
`

export const GALLERY_SETTINGS_ATTRIBUTES = gql`
  fragment gallerySettingsAttributes on GallerySettings {
    defaultCompression
  }
`

export const GET_GALLERY_SETTINGS = gql`
  query gallerySettings_UI {
    gallerySettings {
      ...gallerySettingsAttributes
    }
  }
  ${GALLERY_SETTINGS_ATTRIBUTES}
`

export const UPDATE_GALLERY_SETTINGS = gql`
  mutation updateGallerySettings_UI($input: UpdateGallerySettingsInput!) {
    updateGallerySettings(input: $input) {
      ...gallerySettingsAttributes
    }
  }
  ${GALLERY_SETTINGS_ATTRIBUTES}
`

export const GET_PROJECT_USER_SUGGESTIONS = gql`
  query projectUserSuggestions_UI($projectId: String!) {
    projectSpecificUserSettings(projectId: $projectId) {
      aiSuggestions {
        resource
        resourceId
        resourceName
        nextPromptDate
        hasCreatedToday
      }
      aiSuggestionsLastGenerated
      birthdayWorkerNames
    }
  }
`

export const GENERATE_USER_AI_SUGGESTIONS = gql`
  mutation generateUserAISuggestions_UI($projectId: String!) {
    generateUserAISuggestions(projectId: $projectId)
  }
`

export const PROJECT_SPECIFIC_SITE_DIARY_SETTINGS_ATTRIBUTES = gql`
  fragment projectSpecificSiteDiarySettingsAttributes on ProjectSpecificSiteDiarySettings {
    automaticCreationEnabled
  }
`

export const GET_PROJECT_SPECIFIC_SITE_DIARY_SETTINGS = gql`
  query projectSpecificSiteDiarySettings_UI($projectId: String!) {
    projectSpecificSiteDiarySettings(projectId: $projectId) {
      ...projectSpecificSiteDiarySettingsAttributes
    }
  }
  ${PROJECT_SPECIFIC_SITE_DIARY_SETTINGS_ATTRIBUTES}
`

export const UPDATE_PROJECT_SPECIFIC_SITE_DIARY_SETTINGS = gql`
  mutation updateProjectSpecificSiteDiarySettings_UI(
    $projectId: String!
    $input: UpdateProjectSpecificSiteDiarySettingsInput!
  ) {
    updateProjectSpecificSiteDiarySettings(
      projectId: $projectId
      input: $input
    ) {
      ...projectSpecificSiteDiarySettingsAttributes
    }
  }
  ${PROJECT_SPECIFIC_SITE_DIARY_SETTINGS_ATTRIBUTES}
`
